import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';

// --- Client-side Client using auth-helpers ---
export const createSupabaseClient = (): SupabaseClient => {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseKey) {
    throw new Error('Missing Supabase client environment variables');
  }

  // For client components, use the recommended auth-helpers pattern
  if (typeof window !== 'undefined') {
    return createClientComponentClient();
  } else {
    // For server components (not route handlers), use the basic client
    // This doesn't handle cookies, so don't use for auth operations
    return createClient(supabaseUrl, supabaseKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
      }
    });
  }
};

// --- Admin Client (Server-Side Only) ---
let supabaseAdminInstance: SupabaseClient | null = null;

export const createSupabaseAdminClient = (): SupabaseClient => {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const serviceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;

  if (typeof window !== 'undefined') {
    throw new Error('createSupabaseAdminClient should only be called on the server.');
  }

  if (!supabaseUrl || !serviceKey) {
    throw new Error('Missing Supabase admin environment variables (URL or Service Role Key)');
  }

  if (!supabaseAdminInstance) {
     supabaseAdminInstance = createClient(supabaseUrl, serviceKey, {
       auth: {
         persistSession: false,
         autoRefreshToken: false,
       },
     });
  }
  return supabaseAdminInstance;
};

// Function to help clean corrupted auth state in the browser
export const cleanupAuthState = (): void => {
  if (typeof window === 'undefined') return;
  
  // Clear all localStorage items related to Supabase
  Object.keys(localStorage).forEach(key => {
    if (key.includes('supabase') || key.includes('sb-')) {
      localStorage.removeItem(key);
      console.log('Cleared localStorage item:', key);
    }
  });

  // Clear all Supabase cookies
  document.cookie.split(';').forEach(cookie => {
    const [name] = cookie.trim().split('=');
    if (name.includes('sb-') || name.includes('supabase')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      console.log('Cleared cookie:', name);
    }
  });

  console.log('Auth state cleanup complete. Please refresh the page.');
};

// Optional: Export a pre-initialized client-side instance if needed elsewhere
// export const supabase = createSupabaseClient(); 